import { useState } from 'react';
import type { PhotoClass } from '../../data/database.types';
import type { CapturePhotoView } from '../../data/photos/photo-view';
import { viewsOfClass } from '../../data/photos/photo-view';
import { useCapturePhotoBoard } from './use-capture-photo-board';
import { usePhotoImageUrl } from './use-photo-image-url';
import { DELIVERY_STATE_LABEL } from './photo-messages';
import { PhotoViewer } from './PhotoViewer';
import './photos.css';

/**
 * THE PHOTO SECTION on Capture Detail — design export S12 (M5 / S05-04).
 *
 * One white card, BEFORE above AFTER, 98 px thumbnails, an add action per
 * group, "Remove" under each photo. That layout is the design's, read from the
 * export rather than remembered.
 *
 * **THE CLASS IS ALWAYS CHOSEN BY A HUMAN (PT §9).** There is one add action
 * per group and it carries the class with it, so a photo cannot arrive without
 * someone having said which it is. Nothing here infers Before or After from
 * Capture status, from order, from timing, or from anything else — and there
 * is deliberately no code path that could.
 *
 * **NO PROGRESS CLASS.** Two classes in v1, and adding a third is a product
 * decision, not a widening someone slips into a component.
 *
 * **THE SECTION IS NOT GATED ON STATUS.** Photos may be added, reclassified
 * and removed at any Capture status (PT §9) and while the parent Project is
 * CLOSED (D-029). No status is read here to decide what is allowed, which is
 * the only way to be sure none is.
 *
 * WHAT EACH PHOTO SAYS ABOUT ITSELF: one of four states — waiting, uploading,
 * failed, uploaded — derived in `photo-view.ts` from server truth and the
 * durable queue, never from optimism. A photo that says "Uploaded" has had its
 * full-resolution archive confirmed BY THE SERVER (D-028).
 */
export function CapturePhotoSection({ captureId }: { captureId: string | undefined }) {
  const board = useCapturePhotoBoard(captureId);
  const [viewing, setViewing] = useState<string | null>(null);

  const viewed = board.views.find((view) => view.key === viewing) ?? null;

  return (
    <section className="capture-photos-section" aria-labelledby="capture-photos-heading">
      <h2 id="capture-photos-heading" className="capture-photos-heading">
        PHOTOS
      </h2>

      {board.loading ? (
        <p className="projects-loading" role="status">
          Loading photos…
        </p>
      ) : null}

      {board.loadError ? (
        <p className="cl-error" role="alert">
          {board.loadError}
        </p>
      ) : null}

      {board.notices.length > 0 ? (
        <div className="capture-photos-notices" role="alert">
          {board.notices.map((notice) => (
            <p key={notice} className="capture-photos-notice">
              {notice}
            </p>
          ))}
          <button type="button" className="capture-photos-dismiss" onClick={board.dismissNotices}>
            Dismiss
          </button>
        </div>
      ) : null}

      <PhotoGroup
        photoClass="before"
        views={viewsOfClass(board.views, 'before')}
        board={board}
        onOpen={setViewing}
        emptyMessage="No before photos yet."
      />
      <PhotoGroup
        photoClass="after"
        views={viewsOfClass(board.views, 'after')}
        board={board}
        onOpen={setViewing}
        emptyMessage="No after photos yet — you can add them at any status."
      />

      {/* The design's own sentence, kept verbatim. */}
      <p className="capture-photos-hint">
        Photo type is always chosen by you — never inferred from status.
      </p>

      {viewed ? (
        <PhotoViewer
          view={viewed}
          label={`${viewed.photoClass === 'before' ? 'BEFORE' : 'AFTER'} · ${DELIVERY_STATE_LABEL[viewed.state].toUpperCase()}`}
          onClose={() => {
            setViewing(null);
          }}
        />
      ) : null}
    </section>
  );
}

function PhotoGroup({
  photoClass,
  views,
  board,
  onOpen,
  emptyMessage,
}: {
  photoClass: PhotoClass;
  views: CapturePhotoView[];
  board: ReturnType<typeof useCapturePhotoBoard>;
  onOpen: (key: string) => void;
  emptyMessage: string;
}) {
  const [choosing, setChoosing] = useState(false);
  const title = photoClass === 'before' ? 'Before' : 'After';

  return (
    <div className={`capture-photos-group capture-photos-group--${photoClass}`}>
      <div className="capture-photos-grouphead">
        <span className="capture-photos-grouplabel">
          {title.toUpperCase()} · {views.length}
        </span>
        <button
          type="button"
          className="capture-photos-add"
          disabled={board.adding}
          onClick={() => {
            setChoosing((current) => !current);
          }}
        >
          + Add {title.toLowerCase()}
        </button>
      </div>

      {/* WHERE THE CAMERA AND THE GALLERY DIVERGE, and the only place they do.
          The design does not draw this step (S16 is listed, not exported), so
          it follows the inline idiom the recovery card already uses rather
          than inventing a modal. */}
      {choosing ? (
        <div className="capture-photos-sources">
          <button
            type="button"
            className="capture-photos-source"
            disabled={board.adding}
            onClick={() => {
              setChoosing(false);
              void board.addPhotos('camera', photoClass);
            }}
          >
            Take {title.toLowerCase()} photo
          </button>
          <button
            type="button"
            className="capture-photos-source"
            disabled={board.adding}
            onClick={() => {
              setChoosing(false);
              void board.addPhotos('library', photoClass);
            }}
          >
            Choose from gallery
          </button>
        </div>
      ) : null}

      {board.adding ? (
        <p className="capture-photos-working" role="status">
          Preparing photos…
        </p>
      ) : null}

      <div className="capture-photos-grid">
        {views.map((view, index) => (
          <PhotoTile
            key={view.key}
            view={view}
            ordinal={index + 1}
            groupLabel={title}
            board={board}
            onOpen={onOpen}
          />
        ))}
        {views.length === 0 && !board.adding ? (
          <p className="capture-photos-empty">{emptyMessage}</p>
        ) : null}
      </div>
    </div>
  );
}

function PhotoTile({
  view,
  ordinal,
  groupLabel,
  board,
  onOpen,
}: {
  view: CapturePhotoView;
  ordinal: number;
  groupLabel: string;
  board: ReturnType<typeof useCapturePhotoBoard>;
  onOpen: (key: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  /** Separate from `confirming`: different act, different words, different risk. */
  const [discarding, setDiscarding] = useState(false);
  const url = usePhotoImageUrl(view, 'thumb');
  const name = `${groupLabel} photo ${String(ordinal)}`;
  const otherClass: PhotoClass = view.photoClass === 'before' ? 'after' : 'before';

  return (
    <div className="capture-photo-tile" data-state={view.state} data-key={view.key}>
      <button
        type="button"
        className="capture-photo-thumb"
        aria-label={`View ${name}`}
        onClick={() => {
          onOpen(view.key);
        }}
      >
        {url ? (
          <img className="capture-photo-thumbimage" src={url} alt={name} />
        ) : (
          <span className="capture-photo-thumbplaceholder">{name}</span>
        )}
      </button>

      <p className="capture-photo-state" data-state={view.state}>
        {DELIVERY_STATE_LABEL[view.state]}
      </p>

      {view.state === 'failed' && view.failure ? (
        <p className="capture-photo-failure">{view.failure}</p>
      ) : null}

      {view.state === 'failed' && view.retryable ? (
        <button
          type="button"
          className="capture-photo-retry"
          disabled={board.delivering}
          onClick={() => {
            void board.retryDelivery();
          }}
        >
          Retry {name.toLowerCase()}
        </button>
      ) : null}

      {/* Both act on the SERVER record, so both wait for it to exist. Saying
          why beats a control that silently does nothing. */}
      {view.serverPhotoId === null ? (
        <>
          <p className="capture-photo-pendingnote">
            Before / After can be changed once this photo has uploaded.
          </p>

          {/* **THE ESCAPE HATCH (H-4, closing I-044).** Offered ONLY for a
              photo that is staged locally and has no server record: this is a
              permanent local delete, unlike Remove, which preserves the row and
              the Capture's history. Before this existed the only way out of a
              repeatedly-failing photo was signing out, which took every other
              staged photo with it.

              `view.staged` is the second condition on purpose — a tile with no
              local bytes has nothing to discard, and offering the action there
              would be a control that lies.

              `board.delivering` is the third (H-4 correction 2). While the
              queue is running this control is not shown at all — not shown
              disabled, not shown with an explanation, simply absent, because
              there is nothing the person could usefully do about it and a
              dead button is the control that lies again.

              It is the PASS, not the photo: a photo being retried right now
              still reads 'failed' rather than 'uploading' (nothing clears
              `lastFailure` when an attempt starts), so a per-photo state check
              would leave the hatch open at exactly the wrong moment. The
              matching refusal in `discardStaged` is the rail that holds when
              this markup is edited. */}
          {view.staged !== null && !board.delivering ? (
            discarding ? (
              <div className="capture-photo-confirm">
                <p className="capture-photo-confirmtitle">Discard this staged photo?</p>
                <p className="capture-photo-confirmtext">
                  This photo has not been saved to the project. It will be permanently removed from
                  this device.
                </p>
                <div className="capture-photo-confirmactions">
                  <button
                    type="button"
                    className="capture-photo-keep"
                    onClick={() => {
                      setDiscarding(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="capture-photo-discard"
                    onClick={() => {
                      setDiscarding(false);
                      void board.discardStaged(view);
                    }}
                  >
                    Discard
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="capture-photo-discardstart"
                onClick={() => {
                  setDiscarding(true);
                }}
              >
                Discard staged photo
              </button>
            )
          ) : null}
        </>
      ) : confirming ? (
        <div className="capture-photo-confirm">
          <p className="capture-photo-confirmtext">
            Remove this photo from the Capture? It stops appearing here and in reports. It is not
            deleted — the Capture&rsquo;s history keeps it.
          </p>
          <div className="capture-photo-confirmactions">
            <button
              type="button"
              className="capture-photo-remove"
              onClick={() => {
                setConfirming(false);
                void board.remove(view);
              }}
            >
              Yes, remove it
            </button>
            <button
              type="button"
              className="capture-photo-keep"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <div className="capture-photo-actions">
          <button
            type="button"
            className="capture-photo-reclassify"
            onClick={() => {
              void board.reclassify(view, otherClass);
            }}
          >
            Move to {otherClass === 'before' ? 'Before' : 'After'}
          </button>
          <button
            type="button"
            className="capture-photo-removestart"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
